import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig } from 'ngx-currency';

import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { RateioCentroCusto } from '../../../../../shared/services/models/financas/rateio-centro-custo.model';
import { ConfigFinancasStruct } from '../../../../../shared/services/structs/vendas/config-financas.struct';
import { LookupItemStruct } from '../../../../../shared/services/structs/vendas/lookup-item.struct';
import { UtilService } from '../../../../../shared/services/util.service';
import { diferencaEmCentavos, somarRecebimentos } from './venda-pagamento.calculo';
import { montarFormRecebimento } from './venda-pagamento.form';
import { VdCentroCustoComponent } from './vd-centro-custo/vd-centro-custo.component';
import { VdRecebimentoComponent } from './vd-recebimento/vd-recebimento.component';

/** Como a soma dos recebimentos está frente ao total (§8.0): âmbar, verde ou vermelho. */
export type Cobertura = 'falta' | 'fecha' | 'excede';

/**
 * Listas vazias ESTÁVEIS para quando o agregado ainda não chegou. Um `[]` literal em getter
 * nasceria novo a cada ciclo de detecção e faria o `ngOnChanges` do filho reprocessar sem parar.
 */
const SEM_CENTROS: LookupItemStruct[] = [];
const SEM_RATEIOS: RateioCentroCusto[] = [];

/**
 * Seção 6 — Pagamento (§8), a MESMA nos dois documentos.
 *
 * O documento soma RECEBIMENTOS até cobrir o total (§8.0): esta seção é a lista deles, a linha
 * de acompanhamento (Total · Lançado · Falta lançar/Excede em) com a barra de cobertura, o
 * `Adicionar` que já entra com o saldo restante, e — no fim — o centro de custo e rateio (§8.10).
 *
 * A estrutura é a MESMA nos dois documentos (decisões D7 e D14, e a do Pedro de 16/09: "seção
 * igual nos dois"). O que muda são os textos de apoio e, no orçamento, a caixa de "Condições
 * comerciais" em texto livre, que a proposta imprime.
 *
 * Este componente NÃO valida: a validação do salvamento é `venda-pagamento.validacao.ts`, função
 * pura sobre o form que o shell chama — comunicação por Input/Output, sem o pai chamar método de
 * filho. O que está aqui é só o que a tela mostra e o que o usuário aciona.
 */
@Component({
    selector: 'app-vd-pagamento',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        EmptyStateComponent,
        VdCentroCustoComponent,
        VdRecebimentoComponent,
    ],
    templateUrl: './vd-pagamento.component.html',
    styleUrl: './vd-pagamento.component.css',
})
export class VdPagamentoComponent implements OnInit {
    /** O form da RAIZ: `pagamentos`, `condicoesComerciais`, `idCentroCusto`, `isRateioCentroCusto`, `rateioCentroCusto`. */
    @Input({ required: true }) public form!: FormGroup;
    @Input() public ehOrcamento: boolean = true;

    /** `itens + frete + despesas − desconto`, a MESMA conta da seção de Totais — vem do shell. */
    @Input() public set totalDoDocumento(valor: number) {
        this.totalAtual = valor;
        this.acompanharTotalNoRecebimentoPadrao();
    }

    public get totalDoDocumento(): number {
        return this.totalAtual;
    }

    private totalAtual: number = 0;

    /** Documento ainda não gravado: a seção abre com o recebimento padrão. */
    @Input() public ehDocumentoNovo: boolean = false;

    /**
     * O recebimento que a seção criou ao abrir o documento novo. Enquanto este flag vale, o valor
     * dele acompanha o total; editar o valor, lançar outro recebimento ou excluí-lo encerra o
     * acompanhamento, e o valor passa a ser do usuário.
     */
    private valorAcompanhaTotal: boolean = false;

    /** O bloco `financas` do agregado: formas, condições, intermediadores, contas, categorias, centros, rateios. */
    @Input() public financas: ConfigFinancasStruct | null = null;

    /** Configurações ▸ Vendas ▸ Geral ▸ "Permitir condição de pagamento livre" (§8.5). */
    @Input() public isCondicaoPagamentoLivre: boolean = true;

    @Input() public isSomenteLeitura: boolean = false;

    /** DF10: NSU e observação da parcela obrigatórios quando o contas a receber exige (só no pedido). */
    @Input() public isNsuObrigatorio: boolean = false;
    @Input() public isObservacaoParcelaObrigatoria: boolean = false;

    @Input({ required: true }) public mascaraDinheiro!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraPercentual!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraInteira!: Partial<NgxCurrencyConfig>;

    constructor(private fb: FormBuilder) {}

    /**
     * Documento NOVO já abre com um recebimento: a forma favorita (o `vd-recebimento` a escolhe ao
     * nascer) e o valor igual ao total. Documento gravado fica como veio, mas, se tem um único
     * recebimento no valor do total, ele continua acompanhando o total enquanto o usuário não editar
     * o valor: mudar o preço de um item não pode deixar "Falta lançar" sem o usuário ter mexido no pagamento.
     * Com parcelas geradas, o valor para de acompanhar: mudá-lo por baixo desencontraria a soma das parcelas.
     */
    public ngOnInit(): void {
        if (this.isSomenteLeitura) return;

        if (this.pagamentos.length) {
            const unico = this.pagamentos.length === 1 ? this.pagamentos.at(0) : null;
            const valorUnico = Number(unico?.get('valor')?.value ?? 0) || 0;
            this.valorAcompanhaTotal = unico != null && diferencaEmCentavos(valorUnico, this.totalDoDocumento) === 0;
            return;
        }

        if (!this.ehDocumentoNovo) return;

        const padrao = montarFormRecebimento(this.fb);

        padrao.patchValue({ sequencia: 1, valor: this.totalDoDocumento }, { emitEvent: false });
        this.pagamentos.push(padrao, { emitEvent: false });
        this.valorAcompanhaTotal = true;
    }

    /** `dirty` só vem da digitação: o valor escrito aqui, por código, não conta como edição. */
    private acompanharTotalNoRecebimentoPadrao(): void {
        if (!this.valorAcompanhaTotal) return;

        const valor = this.pagamentos.at(0)?.get('valor');
        const parcelas = this.pagamentos.at(0)?.get('parcelas') as FormArray | null;

        if (!valor || valor.dirty || (parcelas?.length ?? 0) > 0) {
            this.valorAcompanhaTotal = false;
            return;
        }

        valor.setValue(this.totalAtual, { emitEvent: false });
    }

    // ==================================================================== o form

    public get pagamentos(): FormArray {
        return this.form.get('pagamentos') as FormArray;
    }

    public get recebimentos(): FormGroup[] {
        return this.pagamentos.controls as FormGroup[];
    }

    /** A emissão rege os vencimentos das parcelas — lida do form, que já é Input. */
    public get dataEmissao(): Date | string | null {
        return this.form.get('dataEmissao')?.value ?? null;
    }

    public get centrosCusto(): LookupItemStruct[] {
        return this.financas?.listaCentroCusto ?? SEM_CENTROS;
    }

    public get rateiosConfigurados(): RateioCentroCusto[] {
        return this.financas?.listaRateioCentroCusto ?? SEM_RATEIOS;
    }

    // ==================================================================== cobertura (§8.0)

    /** Soma dos valores dos recebimentos, em 2 casas ABNT. */
    public get lancado(): number {
        return somarRecebimentos(this.recebimentos);
    }

    /** Positiva quando falta, negativa quando excede, zero quando fecha. */
    public get diferenca(): number {
        return UtilService.arredondarAbnt(this.totalDoDocumento - this.lancado, 2);
    }

    /** Comparado em CENTAVOS: dinheiro em ponto flutuante não fecha em zero exato. */
    public get cobertura(): Cobertura {
        const centavos = diferencaEmCentavos(this.totalDoDocumento, this.lancado);

        if (centavos > 0) return 'falta';
        if (centavos < 0) return 'excede';

        return 'fecha';
    }

    /** Coberto = fechou OU passou: nos dois casos não há saldo para um recebimento novo. */
    public get totalCoberto(): boolean {
        return this.cobertura !== 'falta';
    }

    /** Preenchimento da barra: 0–100. Sem total, qualquer lançamento já é "além do total". */
    public get percentualCoberto(): number {
        if (this.totalDoDocumento <= 0) return this.lancado > 0 ? 100 : 0;

        return Math.min(100, (this.lancado / this.totalDoDocumento) * 100);
    }

    public get rotuloTotal(): string {
        return 'Total do documento';
    }

    public get rotuloDiferenca(): string {
        return this.cobertura === 'excede' ? 'Excede em' : 'Falta lançar';
    }

    public get valorDiferenca(): number {
        return Math.abs(this.diferenca);
    }

    /** §8.0: o tooltip explica por que Adicionar está desabilitado. Vazio quando está habilitado. */
    public get tooltipAdicionar(): string {
        if (!this.totalCoberto) return '';

        return 'O total do documento já está coberto pelos recebimentos lançados.';
    }

    /**
     * Texto de apoio da seção (§8). Um só para os dois documentos (Pedro, 24/09): a única diferença
     * entre orçamento e pedido é a obrigatoriedade dos recebimentos, e ela mora na validação do salvar.
     */
    public get apoio(): string {
        return 'Some recebimentos até cobrir o total do documento. Cada um carrega os campos da própria forma.';
    }

    // ==================================================================== ações

    /** §8.0: o recebimento novo já entra com o SALDO RESTANTE no valor, sem forma escolhida. */
    public adicionar(): void {
        if (this.isSomenteLeitura || this.totalCoberto) return;

        const novo = montarFormRecebimento(this.fb);

        this.valorAcompanhaTotal = false;
        novo.patchValue({ sequencia: this.pagamentos.length + 1, valor: this.diferenca }, { emitEvent: false });
        this.pagamentos.push(novo);
    }

    /**
     * §8.0: excluir o último deixa a lista VAZIA de fato — nada é reposto. A sequência é
     * renumerada porque ela é a ordem gravada, e um buraco nela não significa nada.
     */
    public remover(indice: number): void {
        this.valorAcompanhaTotal = false;
        this.pagamentos.removeAt(indice);
        this.recebimentos.forEach((r, i) => r.get('sequencia')?.setValue(i + 1, { emitEvent: false }));
    }
}
